/**
 * Utility functions for managing embedded mode state
 * Embedded mode is persistent across sessions using localStorage
 */

const EMBEDDED_MODE_KEY = 'relay_towing_embedded_mode';

// Cache the embedded mode determination for the entire session
let embeddedModeCache: boolean | null = null;

/**
 * Check if the app is currently in embedded mode
 * This is determined once per session by checking URL parameters and localStorage
 * The result is cached for optimal performance on subsequent calls
 */
export function isEmbeddedMode(): boolean {
  // Return cached value if already determined
  if (embeddedModeCache !== null) {
    return embeddedModeCache;
  }
  
  // Determine embedded mode once
  let isEmbedded = false;
  
  // First check URL parameters for explicit embedded setting
  const urlParams = new URLSearchParams(window.location.search);
  const urlEmbedded = urlParams.get('embedded');
  
  if (urlEmbedded !== null) {
    // URL parameter found, use its value and update localStorage
    isEmbedded = urlEmbedded === 'true';
    setEmbeddedMode(isEmbedded);
    console.log(`🔗 [Embedded Mode] URL parameter found: embedded=${urlEmbedded}, cached and saved to localStorage`);
  } else {
    // No URL parameter, check localStorage
    const stored = localStorage.getItem(EMBEDDED_MODE_KEY);
    if (stored !== null) {
      isEmbedded = stored === 'true';
      console.log(`💾 [Embedded Mode] Using stored value: ${isEmbedded}, cached for session`);
    } else {
      // Default to false if nothing is found
      console.log(`❓ [Embedded Mode] No embedded mode found, defaulting to false and caching`);
    }
  }
  
  // Cache the result for the entire session
  embeddedModeCache = isEmbedded;
  return isEmbedded;
}

/**
 * Set the embedded mode state in localStorage
 * This also updates the cache for the current session
 */
export function setEmbeddedMode(isEmbedded: boolean): void {
  localStorage.setItem(EMBEDDED_MODE_KEY, isEmbedded.toString());
  embeddedModeCache = isEmbedded; // Update cache
  console.log(`💾 [Embedded Mode] Saved to localStorage and updated cache: ${isEmbedded}`);
}

/**
 * Explicitly disable embedded mode
 * This will override any URL parameters and set embedded to false
 */
export function disableEmbeddedMode(): void {
  setEmbeddedMode(false);
  console.log(`🚫 [Embedded Mode] Explicitly disabled embedded mode`);
}

/**
 * Explicitly enable embedded mode
 * This will override any URL parameters and set embedded to true
 */
export function enableEmbeddedMode(): void {
  setEmbeddedMode(true);
  console.log(`✅ [Embedded Mode] Explicitly enabled embedded mode`);
}

/**
 * Clear the embedded mode state from localStorage
 * This will make the app fall back to URL parameter detection
 * Also clears the cache so the next call will re-determine the mode
 */
export function clearEmbeddedMode(): void {
  localStorage.removeItem(EMBEDDED_MODE_KEY);
  embeddedModeCache = null; // Clear cache
  console.log(`🗑️ [Embedded Mode] Cleared from localStorage and cache`);
}

/**
 * Clear the embedded mode cache to force re-determination on next call
 * Useful for testing or when the embedded state might have changed
 */
export function clearEmbeddedModeCache(): void {
  embeddedModeCache = null;
  console.log(`🔄 [Embedded Mode] Cache cleared, will re-determine on next call`);
}

/**
 * Get the current embedded mode state as a string for debugging
 */
export function getEmbeddedModeDebugInfo(): string {
  const urlParams = new URLSearchParams(window.location.search);
  const urlEmbedded = urlParams.get('embedded');
  const stored = localStorage.getItem(EMBEDDED_MODE_KEY);
  const current = isEmbeddedMode();
  
  return `Embedded Mode Debug:
  - URL parameter: ${urlEmbedded || 'none'}
  - Stored value: ${stored || 'none'}
  - Cached value: ${embeddedModeCache}
  - Current state: ${current}
  - URL: ${window.location.href}`;
}
